import { z } from 'zod'
import { isDefined, isEmpty } from '@terros/common'
import { type AccountActivityItem, groupSaves, type WorkflowActivity } from './accountActivity'
import { isImportSave } from './historySource'

export const ActivityTimeFormatSchema = z.enum(['relative', 'exact'])

/** How someone likes to read an account's Activity section */
export const AccountActivityPreferencesSchema = z.object({
  oldestFirst: z.boolean(),
  timeFormat: ActivityTimeFormatSchema,
  showAvatars: z.boolean(),
  /** Owner, closer and team changes */
  showAssignments: z.boolean(),
  /** Import saves and updates that came from an integration (API, JobNimbus, CRM status sync) */
  showImports: z.boolean(),
  /** Saves nothing else explains ("updated the account") */
  showOtherUpdates: z.boolean(),
  /** Pre-actions, passed stages, CRM status and distance on workflow updates */
  showDetails: z.boolean(),
})
export type AccountActivityPreferences = z.infer<typeof AccountActivityPreferencesSchema>

export const DEFAULT_ACCOUNT_ACTIVITY_PREFERENCES: AccountActivityPreferences = {
  oldestFirst: true,
  timeFormat: 'relative',
  showAvatars: true,
  showAssignments: true,
  showImports: true,
  showOtherUpdates: true,
  showDetails: true,
}

type FilterAccountActivityProps = {
  items: AccountActivityItem[]
  preferences: AccountActivityPreferences
}

/** Drops what the preferences hide, then regroups saves that end up next to each other */
export const filterAccountActivity = ({ items, preferences }: FilterAccountActivityProps): AccountActivityItem[] =>
  groupSaves(items.flatMap((item) => filterItem({ item, preferences })))

type FilterItemProps = {
  item: AccountActivityItem
  preferences: AccountActivityPreferences
}

const filterItem = ({ item, preferences }: FilterItemProps): AccountActivityItem[] => {
  switch (item.type) {
    case 'owner':
    case 'closer':
    case 'team':
      return preferences.showAssignments ? [item] : []
    case 'workflow':
      return preferences.showImports || !isIntegrationUpdate(item) ? [item] : []
    case 'saves': {
      const actors = item.actors.filter((audit) =>
        isImportSave(audit) ? preferences.showImports : preferences.showOtherUpdates
      )
      return isEmpty(actors) ? [] : [{ ...item, actors }]
    }
    case 'note':
    case 'created':
      return [item]
  }
}

/** A stage change pushed by an integration; the update that created the account always stays */
const isIntegrationUpdate = ({ entry, creation }: WorkflowActivity): boolean => {
  if (isDefined(creation)) return false
  return entry.source === 'api' || entry.source === 'jobNimbus' || isDefined(entry.sourceStatus)
}
